import React, { useState, useRef, useEffect } from 'react';
import { Alert, Col, Form, Row } from 'antd';
import Input from '@lepass/components/uielements/input';
import Router from 'next/router';
import { findCoupon, isCouponDataValid } from '@lepass/lib/helpers/coupon';
import {
  CouponButtonForm,
  CouponCheckboxForm,
  CouponContentWrapper,
  CouponInputDisabled,
} from '@lepass/components/Coupon/Coupon.style';
import { debounce } from 'lodash';

export default function CouponForm({
  couponParam = '',
  lastnameParam = '',
  firstnameParam = '',
  emailParam = '',
  bottomText = <></>,
  buttonText = <>Envoyer</>,
}) {
  const [isLoading, setIsLoading] = useState(false);

  const [coupon, setCoupon] = useState(couponParam);
  const [lastname, setLastname] = useState(lastnameParam);
  const [firstname, setFirstname] = useState(firstnameParam);
  const [email, setEmail] = useState(emailParam);
  const [consent, setConsent] = useState(false);

  const [message, setMessage] = useState('');
  const [form] = Form.useForm();
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const isFormValid = () => {
    return (
      coupon.trim() !== '' &&
      lastname.trim() !== '' &&
      firstname.trim() !== '' &&
      email.trim() !== '' &&
      consent
    );
  };

  const handleForm = async () => {
    if (!isFormValid()) {
      if (isMounted.current) setMessage('');
      return;
    }
    setIsLoading(true);

    const { couponData = undefined, campaignData = undefined } =
      await findCoupon(coupon);

    if (!isMounted.current) return;

    if (couponData === undefined || campaignData === undefined) {
      setMessage("Ce coupon n'est pas valide.");
    } else if (
      couponData.deletedAt !== undefined &&
      couponData.deletedAt != null
    ) {
      setMessage('Ce coupon a été supprimé.');
    } else if (!isCouponDataValid(campaignData, couponData)) {
      setMessage('Ce coupon a déjà été utilisé.');
    } else {
      const now = new Date();
      if (
        now < campaignData.startDate.toDate() ||
        now > campaignData.endDate.toDate()
      ) {
        setMessage("Cette promotion n'est pas disponible.");
      } else {
        setMessage('');
        await Router.push(
          `/promo/${coupon}/payment?lastname=${lastname}&firstname=${firstname}&email=${encodeURIComponent(
            email
          )}`
        );
      }
    }
    if (isMounted.current) setIsLoading(false);
  };

  return (
    <CouponContentWrapper>
      {message && (
        <Alert message={message} type="error" style={{ marginBottom: 20 }} />
      )}
      <Form layout="vertical" form={form}>
        <Row gutter={[8, 8]}>
          <Col xs={24} md={12}>
            <Form.Item
              name="lastname"
              label="Nom"
              rules={[{ required: true, message: 'Saisissez un nom' }]}
              style={{ margin: 0 }}
              initialValue={lastname}
            >
              <Input
                placeholder="Nom"
                onChange={(e) => setLastname(e.target.value)}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="firstname"
              label="Prénom"
              rules={[{ required: true, message: 'Saisissez un prénom' }]}
              style={{ margin: 0 }}
              initialValue={firstname}
            >
              <Input
                placeholder="Prénom"
                onChange={(e) => setFirstname(e.target.value)}
              />
            </Form.Item>
          </Col>
          {/* Pour que le champ email puisse prendre toute la largeur s'il n'y pas le champ coupon */}
          <Col xs={24} md={coupon !== '' ? 24 : 12}>
            <Form.Item
              name="email"
              label="Adresse mail"
              initialValue={email}
              style={{ margin: 0 }}
              rules={[
                { required: true, message: 'Saisissez un email' },
                { type: 'email', message: "L' adresse e-mail est invalide" },
              ]}
            >
              <Input
                placeholder="Adresse mail"
                onChange={(e) => setEmail(e.target.value)}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12} style={{ display: coupon !== '' ? 'none' : '' }}>
            <Form.Item
              name="coupon"
              label="Code unique"
              style={{ margin: 0 }}
              rules={[{ required: true, message: 'Saisissez un code' }]}
              initialValue={coupon}
            >
              <CouponInputDisabled
                placeholder="XX-XXXXXX"
                onChange={(e) => setCoupon(e.target.value)}
                disabled
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="consent"
          valuePropName="checked"
          rules={[
            {
              validator: (_, value) =>
                value
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error('Veuillez accepter les conditions')
                    ),
            },
          ]}
        >
          <CouponCheckboxForm onChange={(e) => setConsent(e.target.checked)}>
            J'accepte les{' '}
            <a href="https://www.lepass.nc" target="_blank">
              conditions Générales d'Utilisation
            </a>{' '}
            du pass.nc, et je confirme avoir lu la{' '}
            <a href="https://www.lepass.nc" target="_blank">
              politique de Confidentialité
            </a>
          </CouponCheckboxForm>
        </Form.Item>

        <div style={{ marginBottom: 0, marginTop: 24, textAlign: 'center' }}>
          {bottomText}
        </div>

        <Form.Item style={{ textAlign: 'center' }}>
          <CouponButtonForm
            type="primary"
            htmlType="submit"
            disabled={isLoading}
            onClick={() => handleForm()}
          >
            {buttonText}
          </CouponButtonForm>
        </Form.Item>
      </Form>
    </CouponContentWrapper>
  );
}
